import { makeAutoObservable, runInAction } from "mobx";
import { kebabToCamel, camelToTitle } from "./utils";
import type RootStore from "stores/RootStore";
import type { ServiceStoreConfig } from "types/ServiceStoreTypes";

interface ColumnHeader {
  key: string;
  title: string;
  dataType: string;
  width?: number;
  disableEdit?: boolean;
}

class ServiceViewStore {
  rootStore: RootStore;
  currentServiceName = "";
  currentServiceConfig: ServiceStoreConfig | null = null;
  columnHeaders: ColumnHeader[] = [];
  isLoading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  setCurrentService(serviceName: string, config: ServiceStoreConfig) {
    this.currentServiceName = serviceName;
    this.currentServiceConfig = config;
    this.generateColumnHeaders();
  }

  generateColumnHeaders() {
    const serviceStoreName = kebabToCamel(this.currentServiceName) + "Store";
    const serviceStore = (this.rootStore as Record<string, unknown>)[serviceStoreName];
    
    if (!serviceStore || typeof serviceStore !== "object" || !("entities" in serviceStore)) {
      this.columnHeaders = [];
      return;
    }

    // Check if custom headers are provided
    if (this.currentServiceConfig?.options?.serviceView?.columnHeaders) {
      this.columnHeaders = this.currentServiceConfig.options.serviceView.columnHeaders;
      return;
    }

    // Generate headers from first entity
    const entities = (serviceStore as { entities: unknown[] }).entities;
    if (!entities || entities.length === 0) {
      this.columnHeaders = [];
      return;
    }

    const firstEntity = entities[0] as Record<string, unknown>;
    const keys = Object.keys(firstEntity);
    
    // Sort keys with ID fields first
    const idFields = keys.filter(key => 
      key.toLowerCase().includes("id") || 
      key === "_id"
    );
    const otherFields = keys.filter(key => 
      !key.toLowerCase().includes("id") && 
      key !== "_id"
    ).sort();
    
    const sortedKeys = [...idFields, ...otherFields];

    this.columnHeaders = sortedKeys.map(key => {
      let dataType = "string";
      const value = firstEntity[key];
      
      // Determine data type based on key name and value
      if (key.toLowerCase().includes("date") || 
          key.toLowerCase().includes("time") ||
          key === "createdAt" || 
          key === "updatedAt") {
        dataType = "date";
      } else if (key.toLowerCase().includes("amount") || 
                 key.toLowerCase().includes("price") || 
                 key.toLowerCase().includes("cost") ||
                 key.toLowerCase().includes("total")) {
        dataType = "currency";
      } else if (typeof value === "boolean") {
        dataType = "boolean";
      } else if (typeof value === "number") {
        dataType = "number";
      }
      
      // Check for boolean field patterns
      if (key.toLowerCase().includes("is") || 
          key.toLowerCase().includes("has") ||
          key.toLowerCase().includes("enabled") ||
          key.toLowerCase().includes("disabled") ||
          key.toLowerCase().includes("archived") ||
          key.toLowerCase().includes("active")) {
        dataType = "boolean";
      }

      return {
        key,
        title: camelToTitle(key),
        dataType,
        width: dataType === "date" ? 150 : dataType === "boolean" ? 100 : 200,
        disableEdit: key.toLowerCase().includes("id") || key === "_id"
      };
    });
  }

  async loadServiceData() {
    if (!this.currentServiceName) return;

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const serviceStoreName = kebabToCamel(this.currentServiceName) + "Store";
      const serviceStore = (this.rootStore as Record<string, unknown>)[serviceStoreName];
      
      if (serviceStore && typeof serviceStore === "object" && "find" in serviceStore) {
        const findMethod = serviceStore.find as (params?: { query?: Record<string, unknown> }) => Promise<unknown>;
        await findMethod({ query: { $limit: 100 } });
        this.generateColumnHeaders();
      }
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load service data";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  get entities() {
    if (!this.currentServiceName) return [];
    
    const serviceStoreName = kebabToCamel(this.currentServiceName) + "Store";
    const serviceStore = (this.rootStore as Record<string, unknown>)[serviceStoreName];
    
    if (serviceStore && typeof serviceStore === "object" && "entities" in serviceStore) {
      return (serviceStore as { entities: unknown[] }).entities || [];
    }
    
    return [];
  }

  get summaryCards() {
    if (!this.currentServiceConfig?.options?.serviceView?.summaryCards) {
      return [];
    }
    
    return this.currentServiceConfig.options.serviceView.summaryCards(this.rootStore);
  }
}

export default ServiceViewStore;